import type { ChefeEstado, EventoAgenda, EventoAtual, EventoProximo, PentaEstado } from '../../api/evento';
import { t } from '../../i18n/idioma';
import { formatar, formatarLongo, restanteDe } from './tempo';

/**
 * Os eventos do jogo, na ordem do quadro (Mais › Eventos, techspec-eventos §08). O id é o mesmo `tipo` da agenda do
 * servidor (`EventoRegras.TipoUltimateBattle`). Evento novo: uma linha aqui, os textos nos 3 idiomas e o seu detalhe.
 */
export interface TipoEvento {
  readonly id: string;
  /** Chave i18n do nome. */
  readonly nome: string;
  readonly selo: SeloId;
}

export type SeloId = 'ub' | 'invasao' | 'chefe' | 'penta';

export const TIPO_UB = 'ultimate-battle';
/** O Chefe do Mundo (techspec-chefe-mundo): o `EventoRegras.TipoChefe` do servidor. */
export const TIPO_CHEFE = 'chefe-mundo';
/** O Despertar da Ponta, do Pentagrama das Cinco Orbes (techspec-pentagrama): o `EventoRegras.TipoPentagrama`. */
export const TIPO_PENTA = 'pentagrama';
const CORES = ['fogo', 'gelo', 'terra', 'ar', 'vazio'];

export const TIPOS_EVENTO: readonly TipoEvento[] = [
  { id: TIPO_UB, nome: 'evento.nome', selo: 'ub' },
  { id: TIPO_CHEFE, nome: 'chefe.nome', selo: 'chefe' },
  { id: TIPO_PENTA, nome: 'penta.nome', selo: 'penta' }
];

/** A ponta desperta agora (o estado do hub, dentro do tempo). */
export function pentaVivo(p: PentaEstado | null | undefined): boolean {
  return !!p && !!p.id && p.cor >= 0 && p.fase < 2 && p.fimEmMs > Date.now();
}

/** Os que vêm aí (techspec-eventos §06, fase 3). Esvaziar a lista some com a seção "Em breve". */
export const EM_BREVE: readonly { nome: string; frase: string; selo: SeloId }[] = [
  { nome: 'evento.breve.invasao.nome', frase: 'evento.breve.invasao.frase', selo: 'invasao' }
];

/** O chefe está de pé (a barra do hub chegou há pouco e ele ainda vive dentro do tempo). */
export function chefeVivo(c: ChefeEstado | null | undefined): boolean {
  return !!c && !!c.id && c.estado === 0 && c.fimEmMs > Date.now();
}

/** A sessão aberta: inscrição (1) ou luta (2), dentro do tempo. */
export function aoVivo(atual: EventoAtual | null): 'inscricao' | 'luta' | null {
  if (!atual) return null;
  if (atual.estado === 1 && atual.fechaEmMs > Date.now()) return 'inscricao';
  if (atual.estado === 2 && (!atual.fimEmMs || atual.fimEmMs > Date.now())) return 'luta';
  return null;
}

/** "hoje", "amanhã" ou o dia da semana do horário, no fuso do jogador. */
export function quandoDe(p: EventoProximo): 'hoje' | 'amanha' | string {
  const d = new Date(p.emMs);
  const hoje = new Date();
  const amanha = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate() + 1);
  if (d.toDateString() === hoje.toDateString()) return 'hoje';
  if (d.toDateString() === amanha.toDateString()) return 'amanha';
  return d.toLocaleDateString(undefined, { weekday: 'short' }).replace('.', '');
}

export function proximoDo(tipo: string, agenda: EventoAgenda | null): EventoProximo | null {
  return agenda?.proximos.find(p => (p.tipo ?? TIPO_UB) === tipo && p.emMs > Date.now()) ?? null;
}

export type TomEvento = 'vivo' | 'breve' | 'agenda' | 'off';

/** O chip de situação do cartão: inscrição aberta, em andamento, começa logo, o próximo horário ou sem horário. */
export function situacaoDe(tipo: string, atual: EventoAtual | null, agenda: EventoAgenda | null, chefe?: ChefeEstado | null,
  penta?: PentaEstado | null): { texto: string; tom: TomEvento } {
  if (tipo === TIPO_PENTA && pentaVivo(penta)) {
    return { texto: t('penta.chip.vivo', { ponta: t(`penta.ponta.${CORES[penta!.cor] ?? 'fogo'}`) }), tom: 'vivo' };
  }
  if (tipo === TIPO_CHEFE && chefeVivo(chefe)) {
    return { texto: t('chefe.chip.vivo', { p: Math.ceil((chefe!.permil) / 10) }), tom: 'vivo' };
  }
  if (tipo === TIPO_UB) {
    const vivo = aoVivo(atual);
    if (vivo === 'inscricao') return { texto: t('evento.chip.inscricoes', { t: formatar(restanteDe(atual!.fechaEmMs)) }), tom: 'vivo' };
    if (vivo === 'luta') return { texto: t('evento.chip.andamento'), tom: 'vivo' };
  }
  const p = proximoDo(tipo, agenda);
  if (!p) return { texto: t('evento.chip.semHorario'), tom: 'off' };
  const s = restanteDe(p.emMs);
  if (s <= 15 * 60) return { texto: t('evento.chip.em', { t: formatarLongo(s) }), tom: 'breve' };
  return { texto: quandoTexto(p, 'chip'), tom: 'agenda' };
}

/** "Hoje · 21:00" (chip) ou "Hoje às 21:00" (frase). */
export function quandoTexto(p: EventoProximo, forma: 'chip' | 'frase'): string {
  const q = quandoDe(p);
  const base = forma === 'chip' ? 'evento.chip' : 'evento.det.quando';
  if (q === 'hoje') return t(`${base}.hoje`, { hora: p.hora });
  if (q === 'amanha') return t(`${base}.amanha`, { hora: p.hora });
  return t(`${base}.dia`, { dia: q, hora: p.hora });
}
